import { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const label = m => new Date(`${m}-01T00:00:00`).toLocaleString('en-IN', { month: 'short', year: 'numeric' });
const inr = n => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const Change = ({ v }) => {
  if (v == null) return <span className="text-slate-300">—</span>;
  const [Icon, cls] = v > 0 ? [TrendingUp, 'text-emerald-700'] : v < 0 ? [TrendingDown, 'text-rose-600'] : [Minus, 'text-slate-400'];
  return <span className={`inline-flex items-center gap-1 font-semibold ${cls}`}><Icon className="w-3.5 h-3.5" />{v > 0 ? `+${v}` : v}</span>;
};

export const HltCompare = ({ month }) => {
  const [d, setD] = useState(null);
  const [filling, setFilling] = useState('');
  const latest = useRef(month);

  const load = useCallback(async () => {
    latest.current = month;
    try {
      let r = (await api.get('/hot-transfer/compare', { params: { month, count: 3 } })).data;
      if (latest.current !== month) return;
      setD(r);
      for (const m of r.missing) {
        setFilling(m);
        try { await api.post('/hot-transfer/refresh', { month: m }); } catch (e) { toast.error(`${m}: ${e.response?.data?.detail || 'CRM read failed'}`); }
        if (latest.current !== month) return;
      }
      if (r.missing.length) { r = (await api.get('/hot-transfer/compare', { params: { month, count: 3 } })).data; if (latest.current === month) setD(r); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load'); }
    finally { if (latest.current === month) setFilling(''); }
  }, [month]);
  useEffect(() => { setD(null); load(); }, [load]);

  if (!d) return <div className="py-16 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const last = d.months[d.months.length - 1];
  return (
    <Card className="p-4" data-testid="hlt-compare-tab">
      <h3 className="text-sm font-bold text-slate-900">Month comparison</h3>
      <p className="text-[11px] text-slate-500 mb-3">Qualified Hot transfers per person, last 3 months ending {label(last)}. Change = {label(last)} vs the month before.</p>
      {filling && <p className="text-xs text-blue-700 mb-3 flex items-center gap-2" data-testid="hlt-compare-filling"><Loader2 className="w-3.5 h-3.5 animate-spin" />Reading {label(filling)} from Zoho CRM (about 30 seconds)…</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-slate-400"><tr>
            <th className="text-left py-1">Emp ID</th><th className="text-left">Employee</th>
            {d.months.map(m => <th key={m} className="text-right">{label(m)}{d.status[m] && d.status[m] !== 'provisional' ? ` · ${d.status[m]}` : ''}</th>)}
            <th className="text-right">Change</th></tr></thead>
          <tbody>
            {d.people.length === 0 && <tr><td colSpan={d.months.length + 3} className="text-center text-slate-400 py-8">{filling ? 'Loading…' : 'No Hot lead transfers in these months.'}</td></tr>}
            {d.people.map(p => (
              <tr key={p.emp_id} className="border-t border-slate-100" data-testid={`hlt-compare-row-${p.emp_id.replace(/[^a-zA-Z0-9]/g, '-')}`}>
                <td className="py-2 font-mono">{p.emp_id}</td><td>{p.name || <span className="text-amber-700">Not linked</span>}</td>
                {d.months.map(m => {
                  const c = p.months[m];
                  return <td key={m} className="text-right">{!d.loaded.includes(m) ? <span className="text-slate-300">not loaded</span> : c ? <><b>{c.qualified}</b> <span className="text-slate-400">/ {c.hot} · {inr(c.amount)}</span></> : <span className="text-slate-300">0</span>}</td>;
                })}
                <td className="text-right"><Change v={p.change} /></td>
              </tr>
            ))}
            {d.people.length > 0 && (
              <tr className="border-t-2 border-slate-200 font-bold" data-testid="hlt-compare-total">
                <td className="py-2" colSpan={2}>Total</td>
                {d.months.map(m => <td key={m} className="text-right">{d.totals[m] ? <>{d.totals[m].qualified} <span className="text-slate-400 font-normal">· {inr(d.totals[m].amount)}</span></> : '—'}</td>)}
                <td className="text-right"><Change v={d.total_change} /></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Bold = qualified (paid) · grey = all Hot transfers · amount.</p>
    </Card>
  );
};
